import { FC, PropsWithChildren, useEffect, useCallback, useMemo } from 'react';
import { ExpandModes, useExpansionContext } from 'utils/contexts/expansion';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import { NestedTableNodeTree, NodeTrees } from '../types';
import { CollapseStateContext, CollapseState } from './CollapseStateContext';
import { useTreeViewContext } from './TreeViewContext';
import { useCreateCollapseState } from './useCreateCollapseState';

const COLLAPSE_STORAGE_KEY = 'json-collapse-:id';

type Props = {
  identifier: string;
  src?: NodeTrees | NestedTableNodeTree[];
};

const shouldCollapse =
  (collapsedState: Record<string, boolean>, collapseByDefault: boolean) => (key: string) =>
    collapsedState[key] ?? collapseByDefault;

export const CollapseStateContextProvider: FC<PropsWithChildren<Props>> = ({
  children,
  identifier,
  src,
}) => {
  const { mode, reset } = useExpansionContext();
  const { collapseByDefault } = useTreeViewContext();
  const { create, merge } = useCreateCollapseState(collapseByDefault);
  const [collapsedState, setCollapsedState] = useLocalStorage<Record<string, boolean>>(
    COLLAPSE_STORAGE_KEY.replace(':id', identifier),
    create(src),
  );

  useEffect(() => {
    setCollapsedState(merge(src));
  }, [src]);

  const isCollapsed = useMemo(
    () => shouldCollapse(collapsedState, collapseByDefault),
    [collapsedState, collapseByDefault],
  );

  const toggle = useCallback(
    (key: string) => {
      setCollapsedState((current) => ({
        ...current,
        [key]: !shouldCollapse(current, collapseByDefault)(key),
      }));
      if (mode !== ExpandModes.default) {
        reset();
      }
    },
    [mode],
  );

  useEffect(() => {
    if (mode === ExpandModes.default) {
      return;
    }

    if (mode === ExpandModes.collapse) {
      setCollapsedState((currentValue) =>
        Object.keys(currentValue).reduce((aggregate, key) => ({ ...aggregate, [key]: true }), {}),
      );
    } else if (mode === ExpandModes.expand) {
      setCollapsedState((currentValue) =>
        Object.keys(currentValue).reduce((aggregate, key) => ({ ...aggregate, [key]: false }), {}),
      );
    } else if (mode === ExpandModes.off) {
      reset();
    }
  }, [mode]);

  const contextValue: CollapseState = {
    toggle,
    isCollapsed,
  };
  return (
    <CollapseStateContext.Provider value={contextValue}>{children}</CollapseStateContext.Provider>
  );
};
